'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductVariant } from '@repo/shopify';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PackageProtectionConfig } from '@/lib/cart-settings';
import { getCurrentBrowserMarketKey, localizePathForCurrentMarket } from '@/lib/markets';
import { formatMoneyAmount } from '@/lib/money';

// ============================================
// Types
// ============================================

export interface SelectedProtectionVariant {
  /** Storefront GID (e.g. gid://shopify/ProductVariant/12345) */
  gid: string;
  /** Price in dollars (e.g. 3.99) */
  price: number;
  /** Currency code */
  currencyCode: string;
  title: string;
}
export interface PackageProtectionState {
  /** The selected variant to add at checkout */
  selectedVariant: SelectedProtectionVariant | null;
  /** Whether the feature is enabled and the cart qualifies */
  isActive: boolean;
  /** Whether variant data is still loading */
  isLoading: boolean;
  /** Formatted price string */
  formattedPrice: string;
}

// ============================================
// Constants
// ============================================

const TARGET_PERCENTAGE = 0.035;
const MIN_CART_VALUE = 10; // $10 minimum cart value in dollars

// ============================================
// Variant cache (module-level, survives re-renders)
// ============================================

interface NormalizedVariant {
  gid: string;
  title: string;
  priceDollars: number;
  currencyCode: string;
}
const variantCache = new Map<string, NormalizedVariant[]>();
const variantRequests = new Map<string, Promise<NormalizedVariant[]>>();
function getPackageProtectionMarketCacheKey() {
  return getCurrentBrowserMarketKey();
}
function normalizeVariants(variants: ProductVariant[]): NormalizedVariant[] {
  return variants.filter(v => v.availableForSale).map(v => ({
    gid: v.id,
    title: v.title,
    priceDollars: Number.parseFloat(v.price.amount),
    currencyCode: v.price.currencyCode
  }));
}

/**
 * Prime the module-level cache from server-prefetched variants. Idempotent —
 * safe to call on every render. Once primed, `fetchProtectionVariants` returns
 * synchronously and no client-side HTTP fetch is issued.
 */
function primeProtectionVariantCache(handle: string, raw: ProductVariant[] | undefined) {
  if (!handle || !raw || raw.length === 0) return;
  const cacheKey = `${getPackageProtectionMarketCacheKey()}|${handle}`;
  if (variantCache.has(cacheKey)) return;
  variantCache.set(cacheKey, normalizeVariants(raw));
}
async function fetchProtectionVariants(handle: string): Promise<NormalizedVariant[]> {
  const cacheKey = `${getPackageProtectionMarketCacheKey()}|${handle}`;
  const cached = variantCache.get(cacheKey);
  if (cached) return cached;
  const existing = variantRequests.get(cacheKey);
  if (existing) return existing;
  const request = fetch(localizePathForCurrentMarket(`/api/products/variants?handle=${encodeURIComponent(handle)}`)).then(async res => {
    if (!res.ok) return [];
    const data = (await res.json()) as {
      variants?: ProductVariant[];
    };
    const variants = normalizeVariants(data.variants ?? []);
    variantCache.set(cacheKey, variants);
    return variants;
  }).catch(() => [] as NormalizedVariant[]).finally(() => {
    variantRequests.delete(cacheKey);
  });
  variantRequests.set(cacheKey, request);
  return request;
}
export async function getSelectedPackageProtectionVariant(config: PackageProtectionConfig, cartSubtotalDollars: number): Promise<SelectedProtectionVariant | null> {
  if (!config.enabled || cartSubtotalDollars < MIN_CART_VALUE) {
    return null;
  }
  primeProtectionVariantCache(config.productHandle, config.variants);
  const variants = await fetchProtectionVariants(config.productHandle);
  if (variants.length === 0) {
    return null;
  }
  const targetPriceCents = Math.round(cartSubtotalDollars * 100 * TARGET_PERCENTAGE);
  const best = selectBestVariant(targetPriceCents, variants);
  if (!best) return null;
  return {
    gid: best.gid,
    price: best.priceDollars,
    currencyCode: best.currencyCode,
    title: best.title
  };
}

// ============================================
// Variant selection logic
// (mirrors the Liquid snippet's algorithm)
// ============================================

function selectBestVariant(targetPriceCents: number, variants: NormalizedVariant[]): NormalizedVariant | null {
  if (!variants.length) return null;
  const withCents = variants.map(v => ({
    variant: v,
    priceCents: Math.round(v.priceDollars * 100)
  }));
  const sorted = withCents.sort((a, b) => a.priceCents - b.priceCents);
  const higher = sorted.filter(v => v.priceCents >= targetPriceCents);
  const lower = sorted.filter(v => v.priceCents < targetPriceCents);
  if (higher.length > 0) {
    const bestHigher = higher[0];
    if (lower.length > 0) {
      const bestLower = lower[lower.length - 1];
      const lowerDiff = targetPriceCents - bestLower.priceCents;
      const higherDiff = bestHigher.priceCents - targetPriceCents;

      // Prefer the lower variant if it's very close
      if (lowerDiff <= 50 && lowerDiff < higherDiff * 0.5) {
        return bestLower.variant;
      }
    }
    return bestHigher.variant;
  }
  return lower.length > 0 ? lower[lower.length - 1].variant : null;
}

// ============================================
// Hook
// ============================================

export function usePackageProtection(config, cartSubtotalDollars) {
  const $ = _c(22);
  primeProtectionVariantCache(config.productHandle, config.variants);
  let t0;
  if ($[0] !== config.productHandle) {
    t0 = () => variantCache.get(config.productHandle) ?? [];
    $[0] = config.productHandle;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [variants, setVariants] = useState(t0);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedHandleRef = useRef(null);
  let t1;
  let t2;
  if ($[2] !== config.enabled || $[3] !== config.productHandle || $[4] !== variants.length) {
    t1 = () => {
      if (!config.enabled) {
        return;
      }
      if (fetchedHandleRef.current === config.productHandle && variants.length > 0) {
        return;
      }
      const cached = variantCache.get(config.productHandle);
      if (cached) {
        setVariants(cached);
        fetchedHandleRef.current = config.productHandle;
        return;
      }
      setIsLoading(true);
      fetchProtectionVariants(config.productHandle).then(fetched => {
        setVariants(fetched);
        fetchedHandleRef.current = config.productHandle;
      }).finally(() => setIsLoading(false));
    };
    t2 = [config.enabled, config.productHandle, variants.length];
    $[2] = config.enabled;
    $[3] = config.productHandle;
    $[4] = variants.length;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  bb0: {
    if (!config.enabled || cartSubtotalDollars < MIN_CART_VALUE || variants.length === 0) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[7] !== cartSubtotalDollars || $[8] !== variants) {
      const targetPriceCents = Math.round(cartSubtotalDollars * 100 * TARGET_PERCENTAGE);
      t4 = selectBestVariant(targetPriceCents, variants);
      $[7] = cartSubtotalDollars;
      $[8] = variants;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const best = t4;
    if (!best) {
      t3 = null;
      break bb0;
    }
    let t5;
    if ($[10] !== best.currencyCode || $[11] !== best.gid || $[12] !== best.priceDollars || $[13] !== best.title) {
      t5 = {
        gid: best.gid,
        price: best.priceDollars,
        currencyCode: best.currencyCode,
        title: best.title
      };
      $[10] = best.currencyCode;
      $[11] = best.gid;
      $[12] = best.priceDollars;
      $[13] = best.title;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t3 = t5;
  }
  const selectedVariant = t3;
  const isActive = config.enabled && cartSubtotalDollars >= MIN_CART_VALUE && selectedVariant !== null;
  let t4;
  if ($[15] !== selectedVariant) {
    t4 = selectedVariant ? formatMoneyAmount(selectedVariant.price, selectedVariant.currencyCode) : "";
    $[15] = selectedVariant;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const formattedPrice = t4;
  let t5;
  if ($[17] !== formattedPrice || $[18] !== isActive || $[19] !== isLoading || $[20] !== selectedVariant) {
    t5 = {
      selectedVariant,
      isActive,
      isLoading,
      formattedPrice
    };
    $[17] = formattedPrice;
    $[18] = isActive;
    $[19] = isLoading;
    $[20] = selectedVariant;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}